/**
 * The yard — `09-equipment/equipment-main.png`.
 *
 * Eighty-four pieces, and the three things somebody scans for are: can it go
 * out, is it due, and is anything wrong with it. So the metric cards are
 * those three, and the list is the shared engine like every other list in
 * Rowted (§3.5).
 */
import { useNavigate } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { ListScreen } from '../components/list-screen.js'
import { Button, StatusChip, Tag } from '../components/primitives.js'
import { RowTray } from '../components/row-tray.js'
import { equipmentMark } from '../components/landing-marks.js'
import { lastActivity, text } from '../components/card-line.js'
import type { CardLine } from '../components/data-list.js'
import { useList, type ListRow } from '../lib/use-list.js'
import { openItems, copyItem } from '../lib/row-menu.js'
import { EM_DASH, formatNumber, formatRate } from '../lib/format.js'
import { EQUIPMENT_STATUS } from '../lib/equipment-types.js'
import { IconPlus, IconTeam } from '../frame/icons.js'

export function Equipment({ me, onNew }: { me: MeResponse; onNew?: () => void }) {
  const list = useList('equipment', {
    alwaysFetch: [
      'status', 'openWorkOrders', 'checkedToday', 'meterKind', 'assignedTo',
      // The mark draws the type's glyph in the type's colour and titles
      // itself with the type's name; the ⋯ menu copies the asset tag, which
      // is what somebody reads off the machine.
      'typeName', 'typeIcon', 'typeColour', 'assetTag',
      /* The card's second and third rows — see `equipmentCardLine`. */
      'model', 'usedBy', 'lastActivityAt',
    ],
  })
  const navigate = useNavigate()

  return (
    <ListScreen
      area="equipment"
      title="Equipment"
      list={list}
      actions={
        <>
          {/*
            * A glyph, because on a phone this row's buttons become squares and
            * a button with only words cannot. Without one it kept its 141px of
            * text and pushed the green button off the right-hand edge.
            */}
          <Button
            onClick={() => navigate('/equipment/assignment')}
            aria-label="Morning assignment"
            title="Morning assignment"
          >
            <IconTeam size={15} />
            Morning Assignment
          </Button>
          {onNew && <Button variant="primary" onClick={onNew}><IconPlus size={15} />New equipment</Button>}
        </>
      }
      primaryColumn={{
        field: 'name',
        header: 'Equipment',
        render: (row: ListRow) => ({
          primary: String(row.name ?? ''),
          /* `model` is already `year, make and model` composed by the area. */
          secondary: [row.assetTag, row.model].filter(Boolean).join(' · ') || undefined,
        }),
      }}
      cardLine={equipmentCardLine}
      cell={{
        status: (row) => <EquipmentStatusChip status={String(row.status ?? '')} />,
        meter: (row) => (row.meter === null || row.meterKind === 'none'
          ? <span className="text-text-faint">{EM_DASH}</span>
          : <span className="tnum">
            {formatNumber(Number(row.meter))}{' '}
            <span className="text-text-muted">{String(row.meterKind)}</span>
          </span>),
        openWorkOrders: (row) => (Number(row.openWorkOrders ?? 0) === 0
          ? <span className="text-text-faint">{EM_DASH}</span>
          : <Tag tone="bad">
            {formatNumber(Number(row.openWorkOrders))} open
          </Tag>),
        assignedTo: (row) => (row.assignedTo
          ? String(row.assignedTo)
          : <span className="text-text-faint">In the yard</span>),
        purchaseCost: (row) => money(row.purchaseCost),
        bookValue: (row) => money(row.bookValue),
        internalHourlyRate: (row) => money(row.internalHourlyRate),
        billableRate: (row) => money(row.billableRate),
      }}
      /*
       * The type's own glyph, tinted by the type's own colour, with a dot for
       * what the shop thinks of it. Aaron: "icons should be selected on
       * categories of equipment not each piece" — so a yard of forty machines
       * reads as trucks, trailers and mowers rather than forty identical grey
       * squares.
       */
      mark={{ render: equipmentMark }}
      tray={(row) => (
        <RowTray
          area="equipment"
          id={row.id}
          actions={[
            { key: 'open', label: 'Open equipment', primary: true,
              onSelect: () => navigate(`/equipment/${row.id}`) },
            { key: 'assignment', label: 'Morning assignment',
              onSelect: () => navigate('/equipment/assignment') },
          ]}
        />
      )}
      rowMenu={(row) => [
        ...openItems(`/equipment/${row.id}`, navigate),
        ...copyItem('copy-tag', 'Copy asset tag', row.assetTag, true),
      ]}
      onRowClick={(row) => navigate(`/equipment/${row.id}`)}
      emptyTitle="Nothing in the yard yet"
      emptyBody="A piece of equipment is anything you assign to a crew or service on a schedule — trucks, trailers, mowers, handhelds."
      emptyAction={onNew ? { label: 'Add equipment', onClick: onNew } : undefined}
      importOrgUnitId={me.scope.selected[0]}
    />
  )
}

const money = (value: unknown) =>
  value === null || value === undefined || value === ''
    ? <span className="text-text-faint">{EM_DASH}</span>
    : formatRate(Number(value))

/**
 * Equipment's status, as the one chip (`marks-and-icons.md` §5).
 *
 * It was two components and neither was `StatusChip`: an outlined `Tag` on the
 * list, and a chip on the record header that borrowed the **job** tokens
 * through a tone — so "In shop" was drawn with Cancelled's grey and the two
 * surfaces showing the same fact did not match each other, let alone the
 * equipment palette that had existed for them all along.
 *
 * One component, the equipment group, and `EQUIPMENT_STATUS` kept for its
 * labels only — which is all a label table should ever have been.
 */
export function EquipmentStatusChip({ status }: { status: string }) {
  return (
    <StatusChip group="equipment" status={status}>
      {EQUIPMENT_STATUS[status]?.label ?? status}
    </StatusChip>
  )
}

/**
 * A machine's third row: **[Available] Z-turn mower · Harrisburg — Sep 26**
 * (v3 §8).
 *
 * The **type's** name, not the machine's: a company owns one kind of Z-turn
 * and forty of the machines, which is the same reason the mark wears the
 * type's glyph rather than this one's. `usedBy` is the branch — the area has
 * named that column since the equipment list was built, and a second field
 * over the same `ou.name` would be two names for one thing.
 */
function equipmentCardLine(row: ListRow): CardLine {
  return {
    chip: <EquipmentStatusChip status={text(row.status)} />,
    facts: [text(row.typeName) || null, text(row.usedBy) || null]
      .filter((part) => part !== null),
    trailing: lastActivity(row),
  }
}
